// Sugalarm — Web Push browser-API helpers (cycle 17).
//
// Pure helpers with no React surface — `usePushSubscriptions` consumes
// them. Keeps service-worker registration and the
// `pushManager.subscribe()` handshake in one spot so the component
// layer stays declarative.

const SW_PATH = "/sw.js";

export function pushSupported(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission(): NotificationPermission {
  if (typeof Notification === "undefined") return "default";
  return Notification.permission;
}

/** Standard helper from the Web Push spec — converts a base64url
 *  VAPID key string to the `Uint8Array` that
 *  `pushManager.subscribe({ applicationServerKey })` expects. */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let _swPromise: Promise<ServiceWorkerRegistration> | null = null;
export function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!pushSupported()) {
    return Promise.reject(new Error("push not supported in this browser"));
  }
  if (_swPromise) return _swPromise;
  _swPromise = navigator.serviceWorker.register(SW_PATH, { scope: "/" });
  return _swPromise;
}

export interface EnableResult {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent: string;
}

export async function enablePush(
  vapidPublicKey: string,
): Promise<EnableResult> {
  if (!pushSupported()) {
    throw new Error("push not supported in this browser");
  }
  if (!vapidPublicKey) {
    throw new Error("missing VAPID public key");
  }
  const reg = await ensureServiceWorker();
  // Wait for the worker to be active before subscribing — Safari is
  // strict about this and throws AbortError if you race it.
  if (reg.installing || reg.waiting) {
    await new Promise<void>((resolve) => {
      const w = reg.installing ?? reg.waiting!;
      const onChange = () => {
        if (w.state === "activated") {
          w.removeEventListener("statechange", onChange);
          resolve();
        }
      };
      if (w.state === "activated") resolve();
      else w.addEventListener("statechange", onChange);
    });
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("notification permission denied");
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast: TS lib widens to `Uint8Array<ArrayBufferLike>` which doesn't
    // line up with PushSubscriptionOptions' BufferSource type even
    // though the runtime accepts it.
    applicationServerKey: urlBase64ToUint8Array(
      vapidPublicKey,
    ) as unknown as BufferSource,
  });
  const json = sub.toJSON();
  const keys = (json.keys ?? {}) as { p256dh?: string; auth?: string };
  if (!json.endpoint || !keys.p256dh || !keys.auth) {
    throw new Error("subscription missing endpoint or keys");
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
    user_agent: navigator.userAgent,
  };
}

/** Unsubscribe in the browser; the caller is responsible for the
 *  matching DELETE on the server. Safe to call when not subscribed. */
/**
 * @param opts.proceed - Cycle 471: a guard RE-READ RIGHT BEFORE `unsubscribe()`.
 *   `teardownAccount` checks that no other account has signed in before it
 *   calls this, but the two local awaits below (`getRegistration`,
 *   `getSubscription`) are a window of their own: a sign-in as B plus a
 *   push-enable tap landing inside them used to unsubscribe B's brand-new
 *   subscription (the cycle-470 review's recorded window; operator decision
 *   at the 470 deploy: not accepted as a permanent gap). When the guard
 *   answers false at the last moment the browser's subscription is left
 *   alone. Omitted (the Settings toggle), the unsubscribe proceeds as before.
 */
export async function disablePush(
  opts: { proceed?: () => boolean } = {},
): Promise<void> {
  if (!pushSupported()) return;
  let reg: ServiceWorkerRegistration | undefined;
  try {
    reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  } catch {
    return;
  }
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    // ⚠ The re-read happens HERE, after the last await and before the
    // unsubscribe — not at entry, where it would be the same check the
    // caller already made.
    if (opts.proceed && !opts.proceed()) return;
    try {
      await sub.unsubscribe();
    } catch {
      /* swallow — best-effort */
    }
  }
}

/** Returns the current browser's subscription endpoint, or null. Used
 *  by the Settings UI to highlight "this device" in the list. */
export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}
